import React, { useEffect, useState } from "react";
import Drawer from "./Drawer";
import TableCheckboxList from "./TableCheckboxList";
import { IoIosSearch, IoIosCloseCircle } from "react-icons/io";
import "./TableCheckboxList.scss"; // Add this import

const CustomizeTable = ({
  title = "Customize table",
  width = "416px",
  isOpen = false,
  onClose = () => { },
  handleSaveTableColumn = () => { },
  handleDefaultSet = () => { },
  searchValue = "",
  onSearchChange = (value: any) => { },
  duplicateColumns = [],
  dynamicColumnsAll = [],
  handleCheckboxChange = (isChecked: boolean, index: number, key: string) => { },
  updateHeaders = (newHeaders: any) => { },
}: any) => {
  const [saveConfiguration, setSaveConfiguration] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Filter and sort columns based on search and checked status
  const filteredAndSortedColumns = React.useMemo(() => {
    // First filter by search
    const filtered = duplicateColumns.filter((column: any) =>
      column.title.toLowerCase().includes(searchValue.toLowerCase())
    );

    // Then separate checked and unchecked columns
    const checkedColumns = filtered.filter((column: any) => column.isChecked);
    const uncheckedColumns = filtered.filter((column: any) => !column.isChecked);

    // Sort unchecked columns alphabetically by title
    const sortedUncheckedColumns = uncheckedColumns.sort((a: any, b: any) =>
      a.title.localeCompare(b.title)
    );

    // Return checked columns first, then sorted unchecked columns
    return [...checkedColumns, ...sortedUncheckedColumns];
  }, [duplicateColumns, searchValue]);

  // Handle select all functionality
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    // Update all non-disabled columns
    duplicateColumns.forEach((column: any, index: number) => {
      if (!column.isDisabled && column.isChecked !== newSelectAll) {
        handleCheckboxChange(newSelectAll, index, column.key);
      }
    });
  };

  // Check if all non-disabled columns are selected
  React.useEffect(() => {
    const nonDisabledColumns = duplicateColumns.filter((col: any) => !col.isDisabled);
    const checkedColumns = nonDisabledColumns.filter((col: any) => col.isChecked);
    setSelectAll(nonDisabledColumns.length > 0 && checkedColumns.length === nonDisabledColumns.length);
  }, [duplicateColumns]);

  useEffect(() => {
    setSaveConfiguration(false)
  }, [isOpen])

  const handleApply = async () => {
    if (!saveConfiguration) return;

    setIsApplying(true);
    try {
      // Pass the selectAll state to handleSaveTableColumn
      await handleSaveTableColumn(selectAll);
      setSaveConfiguration(false);
    } catch (error) {
      console.error('Error applying column changes:', error);
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancel = () => {
    setSaveConfiguration(false);
    onClose();
  };


  const handleResetDefault = async () => {
    setIsApplying(true);
    try {
      await handleDefaultSet();
    } catch (error) {
      console.error('Error resetting to default:', error);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title=""
      width={width}
    >
      <div className="flex flex-col h-full bg-darkcolor-900">
        {/* Header with Reset button */}
        <div className="flex justify-between items-center px-4">
          <h3 className="text-md font-medium text-white">Columns</h3>
          <button
            onClick={handleResetDefault}
            disabled={isApplying}
            className={`text-sm ${isApplying
              ? 'text-gray-500 cursor-not-allowed'
              : 'text-blue-400 hover:text-blue-300 hover:text-primary-500 hover:border-primary-500'
              }`}
          >
            {isApplying ? 'Resetting...' : 'Reset to system default >'}
          </button>
        </div>
        <div className="small-separation-line"></div>

        {/* Search Bar - Using same styling as SearchBar.tsx */}
        <div className="px-2 mb-4">
          <div className="grid items-center pl-[12px] pr-[8px] max-w-full shrink grow min-h-[32px] grid-cols-[auto_1fr] gap-[8px] min-w-[20px] rounded-[6px] border-darkcolor-400 bg-darkcolor-700 border-[1px] border-solid">
            {/* Search Icon */}
            <div>
              <IoIosSearch className="inline-block select-none align-text-bottom overflow-visible text-white" />
            </div>

            {/* Search Input */}
            <div className="relative flex items-center z-[2] h-full w-full" style={{ maxHeight: "unset" }}>
              <div className="w-full h-full">
                <div className={`absolute top-1 left-0 whitespace-pre text-gray-400 text-[14px] ${searchValue ? "invisible" : "visible"}`}>
                  Search
                </div>
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="w-full text-white border-0 p-0 shadow-none flex relative resize-none outline-none bg-transparent caret-white text-[14px] flex-1 h-full"
                  style={{ maxHeight: "unset" }}
                />
              </div>

              {/* Clear Search Button */}
              {searchValue && (
                <div className="flex items-center absolute right-0 z-4">
                  <button
                    className="text-white w-[28px] flex items-center justify-center"
                    style={{ padding: "unset" }}
                    onClick={() => onSearchChange("")}
                    type="button"
                  >
                    <IoIosCloseCircle
                      size={20}
                      className="inline-block select-none align-text-bottom cursor-pointer bg-darkcolor-700 text-gray-400 hover:text-white transition-colors"
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Select All */}
        <div className="px-4 mb-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              disabled={isApplying}
              className={`custom-checkbox ml-2 ${isApplying ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            />
            <label className="ml-3 text-white font-medium text-[14px]">Select all</label>
          </div>
        </div>

        <div className="small-separation-line-new"></div>

        {/* Scrollable Column List */}
        <div className="flex-1 overflow-y-auto px-4 mb-4" style={{ maxHeight: 'calc(100vh - 250px)' }}>
          <TableCheckboxList
            checkboxes={filteredAndSortedColumns}
            onChange={handleCheckboxChange}
            dummyData={dynamicColumnsAll}
            updateHeaders={updateHeaders}
          //disabled={isApplying}
          />
        </div>

        {/* Footer */}
        <div className="border-t border-darkcolor-400 p-4 bg-darkcolor-800">

          {/* Action Buttons - Using same styling as SearchBar.tsx */}
          <div className="flex justify-between items-center gap-3 w-full">
            {/* Left side: Save configuration */}
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={saveConfiguration}
                onChange={(e) => setSaveConfiguration(e.target.checked)}
                disabled={isApplying}
                className={`custom-checkbox ${isApplying ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              />
              <label className="ml-3 text-white text-[14px]">Save this configuration</label>
            </div>

            {/* Right side: Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                disabled={isApplying}
                className={`py-0 px-[8px] h-[32px] gap-[4px] text-[14px] rounded-[6px] border-darkcolor-400 border-[1px] border-solid focus:outline-none ${isApplying
                  ? "bg-darkcolor-600 text-gray-400 cursor-not-allowed"
                  : "bg-darkcolor-700 text-white hover:bg-darkcolor-600"
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={!saveConfiguration || isApplying}
                className={`py-0 px-[8px] h-[32px] gap-[4px] text-[14px] rounded-[6px] border-[1px] border-solid focus:outline-none ${saveConfiguration && !isApplying
                  ? "bg-[#005F7C] border-[#005F7C] text-white hover:bg-[#004A63]"
                  : "bg-darkcolor-600 border-darkcolor-400 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {"Apply"}
              </button>
            </div>
          </div>

        </div>
      </div>
    </Drawer>
  );
};

export default CustomizeTable;
